import {type ChangeEvent, useCallback, useMemo, useRef, memo} from 'react';
import dayjs from 'dayjs';
import {Controller, useFormContext} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {type TFunction} from 'i18next';
import {DATE_FORMAT, FormField, FIELD_TYPES, mergeRefs} from '@chekin/core';
import {getCustomFieldData} from 'pages/AddPersonalDataForm/utils';
import {createGetRequiredOrOptionalFieldLabel} from 'utils/getRequiredOrOptionalFieldLabel';
import {FormTypes} from 'utils/guestForm';
import {useCustomFieldsVirtualKeyboard} from 'components/GuestFormFields/VirtualKeyboard/useCustomFieldsVirtualKeyboard';
import {useLayoutMode} from 'hooks/useLayoutMode';
import Datepicker, {type DatepickerHandle} from 'components/common/Datepicker';
import TimePicker from 'components/common/FieldTimePicker';
import FileInput from 'components/common/FieldFileInput';
import {InputController as Input, Select} from '@guestapp/old-ui-kit';
import {FormFieldWrapper} from 'styled/common';

const ValidFileTypes = ['image/jpeg', 'application/pdf', 'image/png'];
const AcceptableDocuments = '.jpg, .png, .pdf, .jpeg';
const ForbiddenExtensions = ['.svg', '.js', '.exe'];

const validateFileType = (t: TFunction) => (value?: File) => {
  if (!value) return true;

  if (!ValidFileTypes.includes(value.type)) {
    return t('only_doc_file_types');
  }

  const fileExtension = value?.name?.toLowerCase()?.match(/\.[^.]*$/)?.[0];
  if (fileExtension && ForbiddenExtensions.includes(fileExtension)) {
    return t('forbidden_file_type');
  }

  return true;
};

type UseCustomFieldProps = {
  disabled?: boolean;
  field: Omit<FormField, 'id'>;
  required: boolean | string;
};
export const CustomField = memo(({disabled, field, required}: UseCustomFieldProps) => {
  const {t} = useTranslation();
  const {register, control, formState} = useFormContext<FormTypes>();

  const {name, placeholder, label, timeSettings, options} = getCustomFieldData(field);

  const datePickerRef = useRef<DatepickerHandle>(null);

  const {onFocus, onBlur, onFieldFocus, onFieldBlur} = useCustomFieldsVirtualKeyboard(
    name,
    field.type,
    field.type === FIELD_TYPES.date ? datePickerRef : undefined,
  );
  const layoutMode = useLayoutMode();

  const getRequiredOrOptionalFieldLabel = useMemo(
    () => createGetRequiredOrOptionalFieldLabel(t),
    [t],
  );

  const handleFileChange = useCallback(
    (onChange: (value: File | string) => void) =>
      (event: ChangeEvent<HTMLInputElement> | string) => {
        if (!event || typeof event === 'string') {
          return onChange(event);
        }

        const {target} = event;
        if (target?.files?.length) {
          return onChange(target.files[0]);
        }
      },
    [],
  );

  switch (field.type) {
    case FIELD_TYPES.date:
      return (
        <FormFieldWrapper>
          <Controller
            name={name}
            rules={{required}}
            control={control}
            render={({
              field: {onChange, ref: controllerRef, ...field},
              fieldState: {error},
            }) => (
              <Datepicker
                label={getRequiredOrOptionalFieldLabel(label, Boolean(required))}
                error={error?.message}
                onChange={value => {
                  return onChange(dayjs(value?.toString()).format(DATE_FORMAT));
                }}
                disabled={disabled}
                onFieldFocus={onFieldFocus}
                onFieldBlur={onFieldBlur}
                ref={mergeRefs(datePickerRef, controllerRef)}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      );
    case FIELD_TYPES.time:
      return (
        <FormFieldWrapper>
          <Controller
            name={name}
            rules={{required}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <TimePicker
                label={getRequiredOrOptionalFieldLabel(label, Boolean(required))}
                error={error?.message}
                disabled={disabled}
                placeholder={label}
                timeSettings={timeSettings}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      );
    case FIELD_TYPES.file:
      return (
        <FormFieldWrapper>
          <Controller
            name={name}
            rules={{
              required,
              validate: validateFileType(t),
            }}
            control={control}
            render={({field: {onChange, ...restField}, fieldState: {error}}) => (
              <FileInput
                placeholder={getRequiredOrOptionalFieldLabel(
                  placeholder,
                  Boolean(required),
                )}
                onChange={handleFileChange(onChange)}
                label={getRequiredOrOptionalFieldLabel(label, Boolean(required))}
                error={error?.message}
                accept={AcceptableDocuments}
                disabled={disabled}
                {...restField}
              />
            )}
          />
        </FormFieldWrapper>
      );
    case FIELD_TYPES.text:
      return (
        <FormFieldWrapper>
          <Input
            id={name}
            label={getRequiredOrOptionalFieldLabel(label, Boolean(required))}
            {...register(name, {
              required,
            })}
            control={control}
            error={formState.errors[name]?.message as string}
            disabled={disabled}
            autoCorrect="off"
            autoComplete={layoutMode === 'kiosko' ? 'off' : 'on'}
            spellCheck={false}
            onFocus={onFocus}
            onBlur={onBlur}
          />
        </FormFieldWrapper>
      );

    case FIELD_TYPES.select:
      return (
        <FormFieldWrapper>
          <Controller
            name={name}
            rules={{
              required,
            }}
            control={control}
            render={({field, fieldState: {error}}) => (
              <Select
                label={getRequiredOrOptionalFieldLabel(label, Boolean(required))}
                placeholder={t('select')}
                options={options}
                error={error?.message}
                disabled={disabled}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      );
  }

  return null;
});
